import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminService, AdminStateCount, ServiceState } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Filter } from "@/ui/Filter";
import { StateLine, serviceLabel } from "@/ui/Lamp";
import { Input } from "@/ui/Field";
import { Button } from "@/ui/Button";
import { Chip } from "@/ui/Chip";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { ReasonDialog } from "@/features/admin/ReasonDialog";
import { ExportButton } from "@/features/admin/TableActions";
import { useFilters } from "@/features/admin/useFilters";
import { absoluteTime, shortTime } from "@/lib/format";
import { useDebounced } from "@/lib/useDebounced";

export const Route = createFileRoute("/_app/admin/services")({
  component: PlatformServicesScreen,
});

/** What can be done to one service from here. */
type Act = "stop" | "start" | "lock" | "unlock" | "delete";

const ACTS: Record<
  Act,
  {
    label: string;
    title: string;
    description: string;
    confirm: string;
    danger?: boolean;
    toCustomer?: boolean;
  }
> = {
  stop: {
    label: "Stop",
    title: "Stop this service",
    description:
      "The workload scales to nothing. The owner can start it again, which is the difference between this and a hold.",
    confirm: "Stop it",
    toCustomer: true,
  },
  start: {
    label: "Start",
    title: "Start this service",
    description:
      "Asks for the workload back. The cluster has to have room for it.",
    confirm: "Start it",
  },
  lock: {
    label: "Hold",
    title: "Hold this service",
    description:
      "Stops it and keeps it stopped. The owner cannot start, deploy to, scale, change or delete it until the hold is lifted.",
    confirm: "Hold it",
    danger: true,
    toCustomer: true,
  },
  unlock: {
    label: "Lift hold",
    title: "Lift the hold",
    description:
      "Does not start the service. Whatever the owner had asked for survived the hold, so they get it back as they left it.",
    confirm: "Lift it",
  },
  delete: {
    label: "Delete",
    title: "Delete this service",
    description:
      "The service goes and the workload follows at the next reconciliation. The rest of the account is untouched.",
    confirm: "Delete it",
    danger: true,
    toCustomer: true,
  },
};

/**
 * Everything running, across every tenant.
 *
 * Ordered by when a service's state last changed rather than by when it was
 * made, because the interesting service is the one that just went wrong. The
 * counts along the top are there so somebody can see that four things are
 * failing without filtering to find out.
 */
function PlatformServicesScreen() {
  // In the address bar, so "the three services on hold" is a link rather than
  // a set of instructions.
  const filters = useFilters();
  const search = filters.get("q");
  const state = filters.get("state");
  const held = filters.on("held");
  const setSearch = (value: string) => filters.set("q", value);
  const setState = (value: string) => filters.set("state", value);
  const setHeld = (value: boolean) => filters.toggle("held", value);
  const debounced = useDebounced(search, 250);

  // The action in flight, as the service it is about plus what is being done.
  const [acting, setActing] = useState<{
    service: AdminService;
    act: Act;
  } | null>(null);

  const services = useQuery({
    queryKey: ["platform-services", debounced, state],
    queryFn: ({ signal }) =>
      api.get<{
        services: AdminService[] | null;
        states: AdminStateCount[] | null;
      }>(
        "/admin/services",
        {
          search: debounced || undefined,
          state: state || undefined,
          limit: 200,
        },
        signal,
      ),
  });

  const all = services.data?.services ?? [];
  const counts = services.data?.states ?? [];
  // Held services are filtered here rather than in the query: a hold is not a
  // state, and adding it to the state filter would make the counts along the
  // top disagree with the rows underneath them.
  const list = held ? all.filter((s) => s.locked_at) : all;
  const heldCount = all.filter((s) => s.locked_at).length;

  return (
    <div className="flex flex-col gap-4">
      <Panel
        toolbar={
          <>
            {counts.length > 0 && (
              <>
                <Filter
                  label="All"
                  count={counts.reduce((n, c) => n + c.count, 0)}
                  active={state === "" && !held}
                  onClick={() => {
                    setState("");
                    setHeld(false);
                  }}
                />
                {counts.map((c) => (
                  <Filter
                    key={c.state}
                    label={serviceLabel(c.state)}
                    count={c.count}
                    active={state === c.state}
                    onClick={() => {
                      setHeld(false);
                      setState(state === c.state ? "" : c.state);
                    }}
                  />
                ))}
              </>
            )}
            {/* Only when there is one. A permanent "Held 0" chip trains people to
              ignore the place they would look to find out. */}
            {heldCount > 0 && (
              <Filter
                label="On hold"
                count={heldCount}
                tone="down"
                active={held}
                onClick={() => {
                  setState("");
                  setHeld(!held);
                }}
              />
            )}
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <Input
              value={search}
              // Shortened to fit the box it is in. The long version was
              // clipped mid-word — "Search name, tenant or reposito" — which
              // reads as a broken field rather than a hint.
              // Named, not merely hinted. A placeholder disappears the moment
              // somebody types and is not an accessible name, so a screen
              // reader announced an unlabelled box — and a test that located
              // it by placeholder broke the moment the wording changed.
              aria-label="Search services"
              placeholder="Name, tenant or repository"
              className="h-8 w-64 text-[13px]"
              onChange={(e) => setSearch(e.target.value)}
            />
            <ExportButton
              name="drumlin-services"
              rows={list}
              columns={[
                { header: "service", value: (s) => s.display_name },
                { header: "organisation", value: (s) => s.organisation_name },
                { header: "project", value: (s) => s.project_name },
                { header: "environment", value: (s) => s.environment_name },
                { header: "state", value: (s) => s.state },
                { header: "desired_state", value: (s) => s.desired_state },
                {
                  header: "on_hold",
                  value: (s) => (s.locked_at ? (s.locked_reason ?? "yes") : ""),
                },
                { header: "replicas", value: (s) => s.replicas },
                { header: "cpu_millicores", value: (s) => s.cpu_millicores },
                { header: "memory_mib", value: (s) => s.memory_mib },
                { header: "cluster", value: (s) => s.cluster_slug ?? "" },
                { header: "changed", value: (s) => s.state_changed_at },
              ]}
            />
          </div>
        }
        padded={false}
      >
        {services.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={8} columns={6} />
          </div>
        ) : services.isError ? (
          <div className="px-5 py-4">
            <ErrorState
              error={services.error}
              onRetry={() => void services.refetch()}
            />
          </div>
        ) : list.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState
              title="Nothing matches"
              description="No service on this installation matches that."
            />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[24%]">Service</Th>
                  <Th width="w-[18%]">Tenant</Th>
                  <Th width="w-[13%]">State</Th>
                  <Th width="w-[14%]" numeric>
                    Size
                  </Th>
                  <Th width="w-[13%]" align="right">
                    Changed
                  </Th>
                  <Th width="w-[18%]" align="right" quiet>
                    Actions
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <Tr key={s.id}>
                    <Td
                      clip
                      title={`${s.project_name} · ${s.environment_name}`}
                    >
                      <Link
                        to="/o/$orgId/projects/$projectId/services/$serviceId"
                        params={{
                          orgId: s.organisation_id,
                          projectId: s.project_id,
                          serviceId: s.id,
                        }}
                        className="font-medium text-[var(--text-primary)] hover:text-[var(--primary)]"
                      >
                        {s.display_name}
                      </Link>
                      <span className="ml-2 text-[13px] text-[var(--text-muted)]">
                        {s.project_name}
                      </span>
                    </Td>
                    <Td clip title={s.organisation_name}>
                      <Link
                        to="/admin/$orgId"
                        params={{ orgId: s.organisation_id }}
                        className="hover:text-[var(--primary)]"
                      >
                        {s.organisation_name}
                      </Link>
                    </Td>
                    <Td
                      clip
                      title={s.locked_reason || s.state_reason || s.state}
                    >
                      {s.locked_at ? (
                        <Chip tone="down">On hold</Chip>
                      ) : (
                        <StateLine state={s.state as ServiceState} />
                      )}
                    </Td>
                    <Td numeric className="mono text-[13px]">
                      {s.replicas}× {s.cpu_millicores}m
                    </Td>
                    <Td align="right" title={absoluteTime(s.state_changed_at)}>
                      {shortTime(s.state_changed_at)}
                    </Td>
                    <Td actions>
                      <div className="flex items-center justify-end gap-1">
                        {s.locked_at ? (
                          <Act service={s} act="unlock" onPick={setActing} />
                        ) : (
                          <>
                            {s.desired_state === "RUNNING" ? (
                              <Act service={s} act="stop" onPick={setActing} />
                            ) : (
                              <Act service={s} act="start" onPick={setActing} />
                            )}
                            <Act service={s} act="lock" onPick={setActing} />
                          </>
                        )}
                        <Act service={s} act="delete" onPick={setActing} />
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      {acting && (
        <ReasonDialog
          open
          onOpenChange={(open) => !open && setActing(null)}
          title={`${ACTS[acting.act].title}: ${acting.service.display_name}`}
          description={ACTS[acting.act].description}
          confirmLabel={ACTS[acting.act].confirm}
          danger={ACTS[acting.act].danger}
          shownToCustomer={ACTS[acting.act].toCustomer}
          action={(reason) =>
            acting.act === "delete"
              ? // The reason rides in the query string, because DELETE carries
                // no body here. Required rather than merely bounded: an absent
                // optional parameter skips its own minimum length, which is how
                // deletions without a reason used to get through.
                api.delete<void>(
                  `/admin/services/${acting.service.id}`,
                  undefined,
                  {
                    query: { reason },
                  },
                )
              : api.post<void>(
                  `/admin/services/${acting.service.id}/${acting.act}`,
                  { reason },
                )
          }
          onDone={async () => {
            setActing(null);
            await services.refetch();
          }}
        />
      )}
    </div>
  );
}

function Act({
  service,
  act,
  onPick,
}: {
  service: AdminService;
  act: Act;
  onPick: (v: { service: AdminService; act: Act }) => void;
}) {
  return (
    <Button
      tone="secondary"
      size="dense"
      className={ACTS[act].danger ? "text-[var(--danger)]" : undefined}
      onClick={() => onPick({ service, act })}
    >
      {ACTS[act].label}
    </Button>
  );
}

